// Generic dimension annotations: each spec is a measured span A→B drawn as a
// thin line with perpendicular end ticks and a billboarded length pill at its
// midpoint — the same visual language as the on-wall length dimensions. Used for
// the per-axis model size readings (width / depth / height) and the wall-height
// readings in 3D. Text is supplied pre-formatted; pills are held at a constant
// on-screen size (updateScales).

import * as THREE from "three";
import { type SpriteLabel, makeLabel, setLabel } from "./sprite-label";

const TICK = 0.08; // end-tick half-length (world metres), matching the wall labels
const BASE_H = 0.155; // pill on-screen height; width follows the text (l.aspect)

interface V3 {
  x: number;
  y: number;
  z: number;
}
export interface DimSpec {
  a: V3; // span start
  b: V3; // span end
  tick: V3; // half-tick offset (perpendicular to the span); ticks go ±this at each end
  text: string; // pre-formatted, e.g. "1.2 m"
}

export class DimensionLabels {
  readonly group = new THREE.Group();
  private labels: SpriteLabel[] = [];
  private lineGeom = new THREE.BufferGeometry();
  private lines: THREE.LineSegments;

  constructor() {
    const mat = new THREE.LineBasicMaterial({
      color: 0xffe87a,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    this.lines = new THREE.LineSegments(this.lineGeom, mat);
    this.lines.renderOrder = 11; // under the pills (12), over the geometry
    this.lines.frustumCulled = false; // geometry rebuilt each sync; don't cull on a stale bound
    this.group.add(this.lines);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  // Rebuild the lines + pills from the given specs. Pooled sprites are reused;
  // extras are hidden.
  sync(specs: DimSpec[]): void {
    while (this.labels.length < specs.length) {
      const l = makeLabel();
      this.labels.push(l);
      this.group.add(l.sprite);
    }
    const pos: number[] = [];
    for (let i = 0; i < this.labels.length; i++) {
      const on = i < specs.length;
      this.labels[i].sprite.visible = on;
      if (!on) continue;
      const s = specs[i];
      setLabel(this.labels[i], s.text);
      this.labels[i].sprite.position.set(
        (s.a.x + s.b.x) / 2,
        (s.a.y + s.b.y) / 2,
        (s.a.z + s.b.z) / 2,
      );
      pos.push(s.a.x, s.a.y, s.a.z, s.b.x, s.b.y, s.b.z); // main line
      pos.push(
        s.a.x - s.tick.x, s.a.y - s.tick.y, s.a.z - s.tick.z,
        s.a.x + s.tick.x, s.a.y + s.tick.y, s.a.z + s.tick.z,
      ); // start tick
      pos.push(
        s.b.x - s.tick.x, s.b.y - s.tick.y, s.b.z - s.tick.z,
        s.b.x + s.tick.x, s.b.y + s.tick.y, s.b.z + s.tick.z,
      ); // end tick
    }
    this.lineGeom.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  }

  // Hold each pill at a constant on-screen height (caller supplies the per-
  // position factor), width tracking the text via the pill's measured aspect.
  updateScales(scaleAt: (p: THREE.Vector3) => number): void {
    for (const l of this.labels) {
      if (!l.sprite.visible) continue;
      const s = scaleAt(l.sprite.position);
      l.sprite.scale.set(BASE_H * s * l.aspect, BASE_H * s, 1);
    }
  }

  // Default half-tick along an axis (helper for callers building specs).
  static tickX(): V3 {
    return { x: TICK, y: 0, z: 0 };
  }
  static tickZ(): V3 {
    return { x: 0, y: 0, z: TICK };
  }
}
